import React, { useState } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  PencilSquareIcon, 
  CheckCircleIcon,
  EyeIcon,
  XMarkIcon,
  DocumentDuplicateIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';
import { HAProxyTemplate, HAProxyInstance } from '../../types';
import TemplateEditor from './TemplateEditor';
import api from '../../services/api';

interface TemplatesListProps {
  templates: HAProxyTemplate[];
  instances: HAProxyInstance[];
  onAddTemplate: (template: Omit<HAProxyTemplate, 'id' | 'createdAt' | 'updatedAt' | 'lastUsedAt'>) => void;
  onUpdateTemplate: (id: string, template: Omit<HAProxyTemplate, 'id' | 'createdAt' | 'updatedAt' | 'lastUsedAt'>) => void;
  onDeleteTemplate: (id: string) => void;
  onSetDefaultTemplate: (id: string) => void;
  onDeployTemplate: (id: string) => void;
}

const TemplatesList: React.FC<TemplatesListProps> = ({
  templates,
  onAddTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
  onSetDefaultTemplate,
  onDeployTemplate
}) => {
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<HAProxyTemplate | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<HAProxyTemplate | null>(null);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setIsEditorOpen(true);
  };

  const handleEditTemplate = (template: HAProxyTemplate) => {
    setEditingTemplate(template);
    setIsEditorOpen(true);
  };

  const handleShowPreview = async (template: HAProxyTemplate) => {
    setPreviewTemplate(template);
    setPreviewLoading(true);
    setPreviewError(null);
    setShowPreview(true);

    try {
      // Use the backend API to generate the preview
      const response = await api.templates.preview(template.id);
      setPreviewContent(response.content);
    } catch (err) {
      console.error('Failed to generate preview:', err);
      setPreviewError('Failed to generate preview. Please try again later.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDeleteTemplate = (id: string) => {
    setConfirmDelete(id);
  };

  const confirmDeleteTemplate = (id: string) => {
    onDeleteTemplate(id);
    setConfirmDelete(null);
  };

  const handleSaveTemplate = (templateData: Omit<HAProxyTemplate, 'id' | 'createdAt' | 'updatedAt' | 'lastUsedAt'>) => {
    if (editingTemplate) {
      onUpdateTemplate(editingTemplate.id, templateData);
    } else {
      onAddTemplate(templateData);
    }
    setIsEditorOpen(false);
    setEditingTemplate(null);
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setEditingTemplate(null);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-zinc-100 flex items-center">
          <DocumentDuplicateIcon className="h-6 w-6 mr-2 text-indigo-500" />
          HAProxy Configuration Templates
        </h2>
        <button
          type="button"
          onClick={handleAddTemplate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
        >
          <PlusIcon className="h-4 w-4 mr-1.5" />
          New Template
        </button>
      </div>
      
      {/* Description */}
      <div className="bg-zinc-800/50 rounded-lg p-4 text-sm text-zinc-300">
        <p>Templates are used to generate HAProxy configuration files. You can create multiple templates and set one as the default. The default template will be used when deploying configuration to HAProxy instances.</p>
        <p className="mt-2">Use the <code className="bg-zinc-700 px-1.5 py-0.5 rounded text-xs">{"{{BACKENDS}}"}</code> placeholder in your template to specify where dynamically generated backend configurations should be inserted.</p>
      </div>

      {/* Templates list */}
      {templates.length === 0 ? (
        <div className="bg-zinc-800/30 rounded-lg border border-dashed border-zinc-700 p-8 text-center">
          <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-zinc-500" />
          <h3 className="mt-2 text-sm font-medium text-zinc-400">No templates found</h3>
          <p className="mt-1 text-sm text-zinc-500">Get started by creating a new template.</p>
          <div className="mt-6">
            <button
              type="button"
              onClick={handleAddTemplate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
            >
              <PlusIcon className="h-4 w-4 mr-1.5" />
              New Template
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-zinc-800/30 rounded-lg border border-zinc-700 overflow-hidden">
          <table className="min-w-full divide-y divide-zinc-700">
            <thead className="bg-zinc-800/50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Description</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Last Updated</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">Default</th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-zinc-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-700">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-zinc-800/50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-100">{template.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">{template.description}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">{formatDate(template.updatedAt)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-400">
                    {template.isDefault ? (
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : (
                      <button
                        onClick={() => onSetDefaultTemplate(template.id)}
                        className="text-indigo-400 hover:text-indigo-300"
                      >
                        Set as default
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleShowPreview(template)}
                        className="text-indigo-400 hover:text-indigo-300"
                        title="Preview"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className="text-indigo-400 hover:text-indigo-300"
                        title="Edit"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => onDeployTemplate(template.id)}
                        className="text-indigo-400 hover:text-indigo-300"
                        title="Deploy"
                      >
                        <CloudArrowUpIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Template Editor Modal */}
      {isEditorOpen && (
        <TemplateEditor
          template={editingTemplate}
          onSave={handleSaveTemplate}
          onCancel={handleCloseEditor}
        />
      )}

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" onClick={() => setShowPreview(false)}></div>
          <div className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-4xl relative z-10 max-h-[90vh] flex flex-col">
            <div className="border-b border-zinc-800 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-zinc-100">Preview Template: {previewTemplate.name}</h2>
                <button
                  type="button"
                  onClick={() => setShowPreview(false)}
                  className="rounded-md bg-transparent text-zinc-400 hover:text-zinc-200 focus:outline-none"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
            
            <div className="p-6 flex-grow overflow-y-auto">
              {previewLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="flex flex-col items-center">
                    <div className="spinner mb-4"></div>
                    <p className="text-zinc-400">Generating preview...</p>
                  </div>
                </div>
              ) : previewError ? (
                <div className="bg-red-900/30 border border-red-800/50 rounded-lg p-4 text-red-400">
                  {previewError}
                </div>
              ) : (
                <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
                  <pre className="p-4 text-sm text-zinc-300 font-mono overflow-x-auto max-h-[500px]">
                    {previewContent}
                  </pre>
                </div>
              )}
            </div>
            
            <div className="border-t border-zinc-800 px-6 py-4 flex justify-end">
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-zinc-950 bg-opacity-75 backdrop-blur-sm transition-opacity" onClick={() => setConfirmDelete(null)}></div>
          <div className="bg-zinc-900 rounded-xl shadow-2xl overflow-hidden w-full max-w-md relative z-10">
            <div className="p-6">
              <h3 className="text-lg font-medium text-zinc-100 mb-3">Confirm Delete</h3>
              <p className="text-sm text-zinc-300 mb-6">
                Are you sure you want to delete this template? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-zinc-300 bg-zinc-800 hover:bg-zinc-700 focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => confirmDeleteTemplate(confirmDelete)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesList; 